import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { itinerariesData } from '../../mocks/itinerary';
import ItineraryCard from './components/ItineraryCard';
import { agencyInfo } from '../../mocks/dashboard';

const brand = agencyInfo.brandColor;
const STATUS_TABS = ['All', 'Draft', 'Sent', 'Approved'];

export default function ItineraryListPage() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [statusTab, setStatusTab] = useState('All');

  const filtered = itinerariesData.filter((itn) => {
    const matchSearch =
      itn.title.toLowerCase().includes(search.toLowerCase()) ||
      itn.destination.toLowerCase().includes(search.toLowerCase()) ||
      itn.linkedCustomer.toLowerCase().includes(search.toLowerCase());
    const matchStatus = statusTab === 'All' || itn.status === statusTab;
    return matchSearch && matchStatus;
  });

  const totalSell = itinerariesData.flatMap((i) => i.days.flatMap((d) => d.services)).reduce((sum, s) => sum + s.sellPrice, 0);
  const draftCount = itinerariesData.filter((i) => i.status === 'Draft').length;
  const approvedCount = itinerariesData.filter((i) => i.status === 'Approved').length;

  return (
    <div className="p-6 min-h-screen bg-gray-50">
      {/* Page Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-bold text-gray-800">Itinerary Builder</h1>
          <p className="text-sm text-gray-400 mt-0.5">Create beautiful day-by-day travel itineraries for your clients</p>
        </div>
        <button
          onClick={() => navigate('/itinerary/new')}
          className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-white text-sm font-semibold cursor-pointer whitespace-nowrap"
          style={{ backgroundColor: brand }}
        >
          <i className="ri-add-line" />
          New Itinerary
        </button>
      </div>

      {/* KPI Bar */}
      <div className="grid grid-cols-4 gap-4 mb-6">
        {[
          { label: 'Total Itineraries', value: itinerariesData.length, icon: 'ri-file-list-3-line', color: brand },
          { label: 'Drafts', value: draftCount, icon: 'ri-draft-line', color: '#F59E0B' },
          { label: 'Approved', value: approvedCount, icon: 'ri-checkbox-circle-line', color: '#10B981' },
          { label: 'Total Value', value: `AED ${(totalSell / 1000).toFixed(0)}K`, icon: 'ri-money-dollar-circle-line', color: '#6366F1' },
        ].map((kpi) => (
          <div key={kpi.label} className="bg-white rounded-xl border border-gray-100 p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${kpi.color}15`, color: kpi.color }}>
              <i className={`${kpi.icon} text-lg`} />
            </div>
            <div>
              <p className="text-lg font-bold text-gray-800">{kpi.value}</p>
              <p className="text-xs text-gray-400">{kpi.label}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3 mb-5">
        <div className="relative flex-1 max-w-sm">
          <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-300 text-sm" />
          <input
            className="w-full pl-9 pr-3 py-2.5 border border-gray-200 rounded-xl bg-white text-sm text-gray-800 focus:outline-none focus:border-gray-300"
            placeholder="Search itineraries..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div className="flex bg-white border border-gray-200 rounded-xl p-1 gap-1">
          {STATUS_TABS.map((tab) => (
            <button
              key={tab}
              onClick={() => setStatusTab(tab)}
              className="px-3 py-1.5 rounded-lg text-sm font-medium transition-all cursor-pointer whitespace-nowrap"
              style={statusTab === tab ? { backgroundColor: brand, color: '#fff' } : { color: '#6B7280' }}
            >
              {tab}
            </button>
          ))}
        </div>
      </div>

      {/* Grid */}
      {filtered.length === 0 ? (
        <div className="text-center py-20 text-gray-300">
          <i className="ri-map-2-line text-5xl mb-3 block" />
          <p className="text-base font-medium">No itineraries found</p>
          <p className="text-sm mt-1">Create your first itinerary to get started</p>
          <button
            onClick={() => navigate('/itinerary/new')}
            className="mt-4 px-4 py-2.5 rounded-xl text-white text-sm font-semibold cursor-pointer"
            style={{ backgroundColor: brand }}
          >
            Create Itinerary
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {filtered.map((itn) => (
            <ItineraryCard key={itn.id} itinerary={itn} />
          ))}
          {/* New card CTA */}
          <button
            onClick={() => navigate('/itinerary/new')}
            className="border-2 border-dashed border-gray-200 rounded-xl flex flex-col items-center justify-center gap-2 py-12 text-gray-300 hover:border-gray-300 hover:text-gray-400 transition-colors cursor-pointer min-h-[260px]"
          >
            <div className="w-12 h-12 rounded-full border-2 border-dashed border-gray-200 flex items-center justify-center">
              <i className="ri-add-line text-2xl" />
            </div>
            <p className="text-sm font-medium">New Itinerary</p>
          </button>
        </div>
      )}
    </div>
  );
}
